import { useMemo } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { type MRT_ColumnDef } from 'material-react-table';
import { skuTableParams } from '../../../pages/inventoryTableQuery';
import { type SkuRow } from '../../../pages/useInventoryTables';
import ServerTable, { inputStyle, type ServerTableProps } from './ServerTable';
import { idColumn, booleanColumn } from './tableColumns';

const labels = ['THC', 'PCP', 'OXY', 'OPI', 'MTD', 'MET', 'COC', 'BZO', 'BAR', 'AMP', 'ALC', 'BUP', 'COT', 'FYL', 'K2', 'TML'];
export default function SkusTable(props: ServerTableProps<SkuRow>) {
  const columns = useMemo<MRT_ColumnDef<SkuRow>[]>(() => [
    idColumn<SkuRow>(),
    { accessorKey: 'code', header: 'Code', size: 240, filterFn: 'contains', muiFilterTextFieldProps: { placeholder: 'Contains…' },
      Cell: ({ row }) => <Typography fontWeight={700}>{row.original.code}</Typography> },
    { accessorKey: 'strips', header: 'Strips', size: 660, maxSize: 900, enableSorting: false,
      Filter: ({ column }) => {
        const value = (column.getFilterValue() ?? {}) as Record<string, string>;
        return <TextField select value={value.label ?? ''}
            onChange={event => column.setFilterValue(event.target.value ? { label: event.target.value } : undefined)} size="small"
            slotProps={{ select: { displayEmpty: true, inputProps: { 'aria-label': 'Strip label' }, renderValue: value => value ? String(value) : 'All labels' } }} sx={{ ...inputStyle, minWidth: 150 }}
            error={!!props.error?.fields.strip_label} helperText={props.error?.fields.strip_label}>
            <MenuItem value="">All labels</MenuItem>{labels.map(label => <MenuItem key={label} value={label}>{label}</MenuItem>)}
          </TextField>;
      },
      Cell: ({ row }) => {
        const slots = Array.from({ length: 10 }, (_, index) => row.original.strips.find(strip => strip.position === index));
        return <Box data-testid={`Box-9mry-code-${row.original.code}`} sx={{ display: 'flex', gap: 1 }}>
          {slots.map((strip, index) => <Box key={index} data-testid={`Drug-container-${index + 1}-code-${row.original.code}`}>
            {row.index === 0 && <Box data-testid={`Drug-index-Box-${index + 1}-code-${row.original.code}`} sx={{ fontSize: 11, textAlign: 'center' }}>{index + 1}</Box>}
            <Box data-testid={`Drug-Box-${index + 1}-code-${row.original.code}`} sx={{ border: '2px solid', borderColor: strip ? 'primary.main' : 'grey.400',
              borderRadius: 1, width: 45, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" fontWeight={700} data-testid={`Drug-Typography-${index + 1}-code-${row.original.code}`}>{strip?.label ?? ''}</Typography>
            </Box>
          </Box>)}
        </Box>;
      } },
    booleanColumn<SkuRow>('is_active', 'Status', 'Active', 'Inactive'),
  ], [props.error]);
  return <ServerTable {...props} columns={columns} title="SKUs" recordLabel="SKUs" searchPlaceholder="Search code or strip label"
    createLabel="Create SKU" fieldAliases={{ strips: 'strip_' }} rowTestKey={row => `code-${row.code}`} exportEndpoint="skus" exportParams={skuTableParams} />;
}
